import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import createPackDefaults from "../../../Assets/json/createPackDefaults.json"
import { FormState } from "../../../Helpers/formState"
import { PageHeader } from "../../components/ApplicationShell";
import Alert from "../../components/Alert";
import CreatePackWorkflowFrame from "../../components/CreatePackWorkflowFrame";
import { packFormSteps } from "../../components/packFormFixtures";
import Location from "./Location";
import Possession from "./Possession";
import Users from "./Users";
import Forms from "./Forms";
import Keywords from "./Keywords";
import Uploads from "./Uploads";
import {
  faStepForward,
  faStepBackward
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "../../base";
import DesignButton from "../../components/Button";
import IconButton from "../../components/IconButton";
import { formatPossessionRange } from "../../components/PossessionNavigator";
import FormOutline from "../../components/FormOutline";
import CreatePackNav from "../../components/CreatePackNav";
import { cloneDeep, get, isEqual } from "lodash";
import { ElrsService } from "../../../services/elrsService";
import { DraftPackService } from "../../../services/draftPackService";
import { PackService } from "../../../services/packService";
import { LoadingBlock } from "../../components/Loading";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { hasRole, ROLE_NAMES } from "../../../Helpers/roles";
import useValidationErrors from "../../../hooks/useValidationErrors";
import { AlertCircle, CalendarRange, Copy, Layers3, Plus, Tags, Trash2 } from "lucide-react";
import { buildLocationFieldTree, countErrorsForPath } from "./locationFieldTree";
import { clonePossession } from "./clonePossession";
import { shouldUpdateLinkedDate } from "../../../Helpers/linkedDates";

const getModelId = (model) => model?.id || model?._id;
const sections = ['location', 'users', 'forms', 'uploads'];

const aggregatePossessionErrors = (errors) => {
  const value = errors?.['data.possessions'];
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const possessionDateErrorTarget = (message) => {
  const match = String(message).match(/start date of Part\s+(\d+)/i);
  return match ? Number(match[1]) - 1 : null;
};

const possessionIsComplete = (possession, conversionUnit) => {
  if (!possession?.planned_start_date || !possession?.planned_end_date || !possession?.worksites?.length) return false;

  return possession.worksites.every((worksite) => worksite?.worksite_ref && worksite?.lines_at_site?.length &&
    worksite.lines_at_site.every((line) => line?.name && line?.from_elr?.elr && line?.to_elr?.elr &&
      line?.start?.[conversionUnit] !== undefined && line?.end?.[conversionUnit] !== undefined));
};

const parsePackData = (value) => {
  if (!value) return cloneDeep(createPackDefaults);
  if (typeof value === "object") return cloneDeep(value);

  try {
    return JSON.parse(value);
  } catch (error) {
    return cloneDeep(createPackDefaults);
  }
};

const describeSaveError = (error) => {
  if (error?.errors && Object.keys(error.errors).length > 0) {
    return aggregatePossessionErrors(error.errors).find((message) => possessionDateErrorTarget(message) === null) || "";
  }
  return error?.message || "The pack could not be saved. Try again.";
};

const getTempPackData = (record) => {
  if (typeof record?.data !== 'undefined') {
    return parsePackData(record.data);
  }

  const packData = parsePackData(record);
  const { _id, id, user_id, user, created_at, updated_at, ...draftData } = packData;
  return Object.keys(draftData).length > 0 ? draftData : cloneDeep(createPackDefaults);
};

const CreatePack = (props) => {
  const { user: authUser } = useSelector((x) => x.auth);
  const navState = useLocation();
  const { id } = useParams();
  const editing = id ? true : false;
  const [values, setValues] = useState(() => parsePackData(navState?.state?.data));
  const valuesRef = useRef(values);
  const savedValuesRef = useRef(id ? cloneDeep(values) : null);
  const mediaRevisionRef = useRef(Number(values?.media_revision || 0));
  const [toBeUploaded, setToBeUploaded] = useState({});
  const [draftSaveState, setDraftSaveState] = useState(id ? "saved" : "unsaved");
  const [uploadSaveError, setUploadSaveError] = useState("");
  const [saveError, setSaveError] = useState("");
  const { clearErrorsForPath, errors, setErrors } = useValidationErrors({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(Boolean(id));
  const [initialLoadError, setInitialLoadError] = useState("");
  const [activeElrs, setActiveElrs] = useState([]);
  const [elrsLoading, setElrsLoading] = useState(true);
  const [elrsError, setElrsError] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activePossessionIndex, setActivePossessionIndex] = useState(0);
  const [activeLocationFieldId, setActiveLocationFieldId] = useState("location-identification");
  const [displayedPossessionErrorCounts, setDisplayedPossessionErrorCounts] = useState([]);
  const [displayedNestedErrorCounts, setDisplayedNestedErrorCounts] = useState({});
  const [removeRowParams, setRemoveRowParams] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("Are you sure you want to delete this item?");
  const navigate = useNavigate();

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    if (id && editing) {
      setInitialLoading(true);
      setInitialLoadError("");
      setLoading(true);
      DraftPackService.getPackById(id)
        .then((tempPack) => {
          if (tempPack) {
            const savedValues = getTempPackData(tempPack);
            savedValuesRef.current = cloneDeep(savedValues);
            valuesRef.current = savedValues;
            mediaRevisionRef.current = Number(savedValues.media_revision || 0);
            setValues(savedValues);
            setDraftSaveState("saved");
          }
        })
        .catch((error) => {
          setInitialLoadError(error?.message || "The draft pack could not be loaded.");
        })
        .finally(() => {
          setInitialLoading(false);
          setLoading(false);
        });
    }
  }, [editing, id]);

  useEffect(() => {
    setElrsLoading(true);
    setElrsError("");
    ElrsService.getActiveElrs()
      .then((response) => {
        setActiveElrs(Array.isArray(response) ? response : response?.data || []);
      })
      .catch((error) => setElrsError(error?.message || "ELR options could not be loaded."))
      .finally(() => setElrsLoading(false));
  }, []);

  useEffect(() => {
    if (!values?.location?.is_multi_possession) {
      const possessions = valuesRef.current.possessions || [];
      if (possessions.length <= 1) 
        return;
      const nextValues = FormState.changeValuesByPath(valuesRef.current, "possessions", [cloneDeep(possessions[0])]);
      valuesRef.current = nextValues;
      setValues(nextValues);
    }
  }, [values?.location?.is_multi_possession]);

  const handleInputChange = useCallback((path, e, options = {}) => {
    const value = typeof e?.target?.value !== 'undefined' ? e.target.value : e;
    if (isEqual(get(valuesRef.current, path), value)) return;
    clearErrorsForPath(path);

    const nextValues = FormState.changeValuesByPath(valuesRef.current, path, value);
    valuesRef.current = nextValues;
    setValues(nextValues);
    if (options.systemSync) {
      if (savedValuesRef.current) {
        savedValuesRef.current = FormState.changeValuesByPath(savedValuesRef.current, path, cloneDeep(value));
      }
      return;
    }
    setDraftSaveState(savedValuesRef.current && isEqual(nextValues, savedValuesRef.current) ? "saved" : "unsaved");
  }, [clearErrorsForPath]);

  const handleAddFormRow = (path, template) => {
    const existingData = cloneDeep(get(values, path, []));
    const updatedData = Array.isArray(existingData) ? [...existingData, template] : [template];
  
    handleInputChange(path, updatedData);
  };
  
  const handleRemoveFormRow = (path, index, requireConfirmation = false, section = "item") => {
    if (requireConfirmation) {
      setRemoveRowParams({ path, index });
      setConfirmMessage(`Are you sure you want to remove this ${section}?`);
      setShowConfirmModal(true);
      return;
    }
  
    removeRow(path, index);
  };
  
  const removeRow = (path, index) => {
    const existingData = cloneDeep(get(values, path, []));
    if (!Array.isArray(existingData) || index < 0 || index >= existingData.length) return;
  
    const updatedData = existingData.filter((_, i) => i !== index);
    handleInputChange(path, updatedData);

    // Removing the last part moves the pack end back, so keep the final ES shift end linked to it.
    const esUsers = values?.users?.esusers || [];
    const newPackEnd = updatedData[updatedData.length - 1]?.planned_end_date;
    if (path === "possessions" && index === existingData.length - 1 && esUsers.length > 0 && newPackEnd) {
      const finalEsIndex = esUsers.length - 1;
      if (shouldUpdateLinkedDate(esUsers[finalEsIndex].end_date, existingData[index].planned_end_date)) {
        handleInputChange(`users.esusers.${finalEsIndex}.end_date`, newPackEnd);
      }
    }
  };
  
  const confirmRemoveRow = () => {
    if (removeRowParams) {
      removeRow(removeRowParams.path, removeRowParams.index);
    }
    setShowConfirmModal(false);
    setRemoveRowParams(null);
  };

  const handleCreatePack = (e) => {
      e.preventDefault();
  
      const obj = cloneDeep(values);

      setLoading(true);
      setDraftSaveState("saving");
      setSaveError("");
      DraftPackService.createPack(obj)
        .then((response) => {
          const tempPackId = getModelId(response?.data);
          if (response.success && tempPackId) {
            const savedValues = getTempPackData(response.data);
            savedValuesRef.current = cloneDeep(savedValues);
            valuesRef.current = savedValues;
            setValues(savedValues);
            setDraftSaveState("saved");
            setLoading(false);
            navigate("/packs/create/" + tempPackId, {
              replace: true,
              state: { draftData: response.data },
            });
          } else {
            setSaveError(response?.message || "The pack could not be saved. Try again.");
            setDraftSaveState("failed");
            setLoading(false);
          }
        })
        .catch((error) => {
          if (error.current_section) {
            handleInputChange('current_section', error.current_section);
          }
          setErrors(error.errors ?? {});
          setSaveError(describeSaveError(error));
          setDraftSaveState("failed");
          setLoading(false);
        });
    };
  
  const handleCompletePack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDraftSaveState("saving");
    setSaveError("");

    let mediaResponse;
    try {
      mediaResponse = await saveUploads();
    } catch (requestError) {
      setUploadSaveError(requestError?.message || "The attachment changes could not be saved. Try again.");
      setDraftSaveState("failed");
      setLoading(false);
      return;
    }
    const obj = cloneDeep(values);

    if (mediaResponse) {
      obj.media = mediaResponse;
    }

    PackService.createPack({ id, data: obj })
    .then((response) => {
      const packId = getModelId(response?.data);
      if (response.success && packId) {
        navigate("/view/" + packId);
      } else {
        setSaveError(response?.message || "The pack could not be saved. Try again.");
        setDraftSaveState("failed");
        setLoading(false);
      }
    })
    .catch((error) => {
      if (error.current_section) {
        handleInputChange('current_section', error.current_section);
      }
      setErrors(error.errors ?? {});
      setSaveError(describeSaveError(error));
      setDraftSaveState("failed");
      setLoading(false);
    });
  };
  
  const saveUploads = useCallback(async () => {
    const uploads = cloneDeep(toBeUploaded);
    
    if (Object.keys(uploads).length > 0 && values?.media?.length > 0) uploads.existing_media = values.media;
  
    if (Object.keys(uploads).length !== 0) {
      const requestRevision = mediaRevisionRef.current + 1;
      mediaRevisionRef.current = requestRevision;
      const response = await DraftPackService.saveUploads(id, { ...uploads, media_revision: requestRevision });
      const data = response?.media ?? response;
      if (Array.isArray(data)) {
        setValues((current) => ({
          ...FormState.changeValuesByPath(current, 'media', data),
          media_revision: response?.media_revision ?? requestRevision,
        }));
        setToBeUploaded({});
      }
      return data;
    }
    return null;
  }, [id, toBeUploaded, values?.media]);

  const saveAttachmentSelection = useCallback(async (type, files) => {
    const retainedFiles = files.filter((file) => !(file instanceof Blob));
    const queuedFiles = files.filter((file) => file instanceof Blob);
    const existingMedia = [
      ...(values?.media || []).filter((file) => file.collection !== type.collection),
      ...retainedFiles,
    ];
    const pending = queuedFiles.length ? { [type.field]: queuedFiles } : {};

    setToBeUploaded((current) => {
      const next = { ...current };
      if (queuedFiles.length) next[type.field] = queuedFiles;
      else delete next[type.field];
      return next;
    });
    setUploadSaveError("");
    setDraftSaveState("saving");
    const requestRevision = mediaRevisionRef.current + 1;
    mediaRevisionRef.current = requestRevision;
    try {
      const response = await DraftPackService.saveUploads(id, {
        ...pending,
        existing_media: existingMedia,
        media_revision: requestRevision,
      });
      if (requestRevision !== mediaRevisionRef.current) return;
      const media = response?.media ?? response ?? [];
      const savedValues = {
        ...FormState.changeValuesByPath(valuesRef.current, "media", media),
        media_revision: response?.media_revision ?? requestRevision,
      };
      savedValuesRef.current = cloneDeep(savedValues);
      valuesRef.current = savedValues;
      setValues(savedValues);
      setToBeUploaded((current) => {
        const next = { ...current };
        delete next[type.field];
        return next;
      });
      setDraftSaveState("saved");
    } catch (requestError) {
      if (requestRevision !== mediaRevisionRef.current) return;
      const message = requestError instanceof TypeError
        ? "The attachment changes could not be saved. Check your connection and try again."
        : requestError?.message || "The attachment changes could not be saved. Try again.";
      setUploadSaveError(message);
      setDraftSaveState("failed");
    }
  }, [id, values?.media]);

  const openExistingFile = useCallback(async (file) => {
    const response = await DraftPackService.getUpload(id, file);
    if (!response?.success || !response?.base64) return;
    const link = document.createElement("a");
    link.href = `data:${file.mime_type || "application/pdf"};base64,${response.base64}`;
    link.download = file.name;
    link.click();
  }, [id]);

  const handleEditPack = useCallback(async (direction, section) => {
    setLoading(true);
    setDraftSaveState("saving");
    setSaveError("");

    let mediaResponse;
    try {
      mediaResponse = await saveUploads();
    } catch (requestError) {
      setUploadSaveError(requestError?.message || "The attachment changes could not be saved. Try again.");
      setDraftSaveState("failed");
      setLoading(false);
      return;
    }
  
    let obj = cloneDeep(values);
  
    let current_stage = sections.indexOf(obj.current_section);
    if (direction) {
      obj.next_section = direction === 'forward' ? sections[current_stage + 1] : sections[current_stage - 1];
    } else if (section) {
      obj.next_section = section;
    }

    if (mediaResponse) {
      obj.media = mediaResponse
    }
  
    DraftPackService.updatePack(id, obj)
      .then((response) => {
        if (response.success) {
          const savedValues = response.data
            ? getTempPackData(response.data)
            : { ...obj, current_section: obj.next_section };
          delete savedValues.next_section;
          savedValuesRef.current = cloneDeep(savedValues);
          valuesRef.current = savedValues;
          setValues(savedValues);
          setErrors({});
          setDraftSaveState("saved");
          setLoading(false);
        } else {
          setSaveError(response?.message || "The pack could not be saved. Try again.");
          setDraftSaveState("failed");
          setLoading(false);
        }
      })
      .catch((error) => {
        if (error.current_section) {
          handleInputChange('current_section', error.current_section);
        }
        setErrors(error.errors ?? {});
        setSaveError(describeSaveError(error));
        setDraftSaveState("failed");
        setLoading(false);
      });
  }, [handleInputChange, id, saveUploads, setErrors, values]);

  const currentSection = sections.includes(values?.current_section) ? values.current_section : 'location';
  const saveErrorAlert = saveError && <Alert title="Pack not saved" type="error">{saveError}</Alert>;
  const saveStatus = id ? {
    failed: "Draft not saved",
    saved: "Draft saved",
    saving: "Saving draft\u2026",
    unsaved: "Unsaved changes",
  }[draftSaveState] : "Not saved yet";

  useEffect(() => {
    const shouldWarn = ["unsaved", "saving", "failed"].includes(draftSaveState);
    if (!shouldWarn) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [draftSaveState]);

  const workflowSteps = packFormSteps.map((step) => ({
    ...step,
    title: { location: 'Location and pack details', users: 'Users', forms: 'Forms', uploads: 'Uploads' }[step.id],
    longDescription: {
      location: 'Enter the possession reference, planned dates, and measurement format for this pack.',
      users: 'Assign the people responsible for delivering this worksite pack.',
      forms: 'Complete the operational records required for this worksite pack.',
      uploads: 'Add the supporting files required before creating the pack.',
    }[step.id],
  }));
  const submitEvent = { preventDefault: () => {} };
  const handleWorkflowNext = () => {
    if (currentSection === 'uploads') return handleCompletePack(submitEvent);
    if (!id) return handleCreatePack(submitEvent);
    return handleEditPack('forward');
  };
  const handleWorkflowPrevious = id && currentSection !== 'location' ? () => handleEditPack('back') : undefined;
  const handleWorkflowStepSelect = id ? (section) => section !== currentSection && handleEditPack(null, section) : undefined;
  const possessions = useMemo(() => values?.possessions || [], [values?.possessions]);
  const aggregateErrors = aggregatePossessionErrors(errors);
  useEffect(() => {
    const counts = possessions.map((_, index) => {
      const target = document.getElementById(`possession-section-${index + 1}`);
      if (!target) return 0;
      return target.querySelectorAll(".wsm-field__error, .wsm-date-range__error, .input__error, .wsm-alert--error").length;
    });
    setDisplayedPossessionErrorCounts(counts);

    const nestedCounts = {};
    possessions.forEach((possession, possessionIndex) => {
      nestedCounts[`possession-${possessionIndex}`] = document.getElementById(`possession-${possessionIndex + 1}-period-error`) ? 1 : 0;
      (possession.worksites || []).forEach((worksite, worksiteIndex) => {
        nestedCounts[`worksite-${possessionIndex}-${worksiteIndex}`] = document.getElementById(`worksite-ref-${possessionIndex}-${worksiteIndex}-error`) ? 1 : 0;
        (worksite.lines_at_site || []).forEach((_, lineIndex) => {
          const lineTarget = document.getElementById(`line-section-${possessionIndex}-${worksiteIndex}-${lineIndex}`);
          nestedCounts[`line-${possessionIndex}-${worksiteIndex}-${lineIndex}`] = lineTarget
            ? lineTarget.querySelectorAll(".wsm-field__error, .wsm-date-range__error, .input__error, .wsm-alert--error").length
            : 0;
        });
      });
    });
    setDisplayedNestedErrorCounts(nestedCounts);
  }, [errors, possessions]);
  const addPossession = useCallback(() => {
    const newPossession = cloneDeep(createPackDefaults.possessions[0]);
    const previousPossession = possessions[possessions.length - 1];
    if (previousPossession?.planned_end_date) {
      newPossession.planned_start_date = previousPossession.planned_end_date;
    }
    handleAddFormRow("possessions", newPossession);
  }, [handleAddFormRow, possessions]);
  const handleClonePossession = (possessionIndex) => {
    const previousPossession = possessions[possessions.length - 1];
    handleAddFormRow("possessions", clonePossession(possessions[possessionIndex], previousPossession?.planned_end_date));
    setActivePossessionIndex(possessions.length);
  };
  const locationFieldTree = useMemo(
    () => buildLocationFieldTree({
      errors,
      nestedErrorCounts: displayedNestedErrorCounts,
      onAddPossession: addPossession,
      values,
    }),
    [addPossession, displayedNestedErrorCounts, errors, values],
  );
  const possessionNavigationItems = possessions.map((possession, index) => ({
    complete: possessionIsComplete(possession, values?.conversion_unit || "milesYards"),
    end: possession.planned_end_date,
    errorCount: displayedPossessionErrorCounts[index] ?? countErrorsForPath(errors, `data.possessions.${index}`),
    id: `possession-${index + 1}`,
    start: possession.planned_start_date,
  }));
  const selectLocationField = (item) => {
    setActiveLocationFieldId(item.id);
    if (Number.isInteger(item.possessionIndex)) setActivePossessionIndex(item.possessionIndex);
    const target = document.getElementById(item.targetId) ||
      (Number.isInteger(item.possessionIndex) ? document.getElementById(`possession-section-${item.possessionIndex + 1}`) : null);
    if (!target) return;
    const scrollTarget = target.closest(".wsm-field, .wsm-date-range") || target;
    const mobileTrigger = document.querySelector(".wsm-form-outline__trigger");
    const triggerVisible = mobileTrigger && window.getComputedStyle(mobileTrigger).display !== "none";
    const stickyTop = triggerVisible ? Number.parseFloat(window.getComputedStyle(mobileTrigger).top) || 0 : 0;
    const clearance = triggerVisible ? stickyTop + mobileTrigger.offsetHeight + 20 : 96;
    const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - clearance;
    window.scrollTo({ behavior: "smooth", top: Math.max(0, targetTop) });
    window.setTimeout(() => {
      const focusTarget = target.matches("input, select, textarea, button") ? target : target.querySelector("input, select, textarea, button");
      (focusTarget || target).focus({ preventScroll: true });
    }, 350);
  };

  return (
    <>
      <PageHeader
        description="Build a worksite pack step by step. Your progress is saved as a draft."
        title="Create pack"
      />
      <CreatePackWorkflowFrame
        currentStep={currentSection}
        loading={loading || draftSaveState === "saving"}
        onNext={handleWorkflowNext}
        onPrevious={handleWorkflowPrevious}
        onStepSelect={handleWorkflowStepSelect}
        saveStatus={saveStatus}
        steps={workflowSteps}
      >
        <div className="page schedule-rest-day-page wsm-create-pack-migrated">
      <div className="page__content">
        {initialLoading && <LoadingBlock label="Loading draft pack" lines={8} />}
        {!initialLoading && initialLoadError && <Alert title="Unable to load draft pack" type="error">{initialLoadError}</Alert>}
        {!initialLoading && !initialLoadError && <div className="form__section" aria-busy={loading || undefined}>
          {elrsError && <Alert title="Unable to load ELR options" type="error">{elrsError} You can continue with other sections, but ELR fields will be unavailable.</Alert>}
          <div className="pack-section">
            <CreatePackNav
              loading={loading}
              editing={editing && id}
              sectionView={values?.current_section}
              handleCreatePack={handleCreatePack}
              handleEditPack={handleEditPack}
            />
          </div>

          <div className="pack-section">
            <div className="create-pack-page">
              <div className="page__content">
                {values?.current_section === 'location' && <div className="wsm-location-form-layout">
                  <FormOutline activeId={activeLocationFieldId} items={locationFieldTree} onSelect={selectLocationField} />
                  <div className="wsm-location-form-layout__content">
                  <Location
                    values={values}
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />

                  <section className="wsm-pack-form-section">
                    <div className="wsm-pack-form-section__heading">
                      <div className="wsm-pack-form-section__heading-title">
                        <span className="wsm-pack-form-section__heading-icon"><Layers3 aria-hidden="true" /></span>
                        <div>
                        <h3 id="possessions-heading" tabIndex="-1">Possession</h3>
                        <p>Set the operational dates and configure the worksites covered by this pack.</p>
                        </div>
                      </div>
                    </div>
                  {values.location?.is_multi_possession === 1 && (
                    <div className="wsm-possession-list__actions">
                      <DesignButton icon={Plus} onClick={addPossession} variant="outline">Add part</DesignButton>
                    </div>
                  )}
                  <div className="wsm-possession-list">
                    {possessions.map((possession, possessionIndex) => {
                      const errorCount = possessionNavigationItems[possessionIndex]?.errorCount || 0;
                      return (
                      <article
                        className={["wsm-possession-item", errorCount && "wsm-possession-item--error", activePossessionIndex === possessionIndex && "wsm-possession-item--active"].filter(Boolean).join(" ")}
                        id={`possession-section-${possessionIndex + 1}`}
                        key={`possession-${possessionIndex}`}
                        tabIndex="-1"
                      >
                        <div className="wsm-possession-item__heading">
                          <div className="wsm-possession-item__title">
                            <span><CalendarRange aria-hidden="true" /></span>
                            <div>
                              <strong>Part {possessionIndex + 1}</strong>
                              <small>{formatPossessionRange(possession.planned_start_date, possession.planned_end_date)}</small>
                            </div>
                          </div>
                          <div className="wsm-possession-item__actions">
                            {errorCount > 0 && <span className="wsm-possession-item__error-count"><AlertCircle aria-hidden="true" />{errorCount} {errorCount === 1 ? "error" : "errors"}</span>}
                            {values.location?.is_multi_possession === 1 && <IconButton aria-label={`Duplicate part ${possessionIndex + 1}`} icon={Copy} onClick={() => handleClonePossession(possessionIndex)} size="compact" variant="ghost" />}
                            <IconButton aria-label={`Remove part ${possessionIndex + 1}`} disabled={possessionIndex === 0} icon={Trash2} onClick={() => handleRemoveFormRow("possessions", possessionIndex, true, "part")} size="compact" variant="destructive" />
                          </div>
                        </div>
                        <div className="wsm-possession-item__body">
                          <Possession
                            aggregateDateError={aggregateErrors.find((message) => possessionDateErrorTarget(message) === possessionIndex)}
                            activeElrs={activeElrs}
                            conversionUnit={values?.conversion_unit}
                            elrsLoading={elrsLoading}
                            errors={errors}
                            handleAddFormRow={handleAddFormRow}
                            handleInputChange={handleInputChange}
                            handleRemoveFormRow={handleRemoveFormRow}
                            levelCrossingTemplate={createPackDefaults.possessions[0].level_crossings[0]}
                            markerBoardTemplate={createPackDefaults.possessions[0].marker_boards[0]}
                            possessionData={possession}
                            possessionIndex={possessionIndex}
                            setValues={setValues}
                            values={values}
                            worksiteTemplate={createPackDefaults.possessions[0].worksites[0]}
                          />
                        </div>
                      </article>
                    );})}
                  </div>
                  </section>

                  <section className="wsm-pack-form-section">
                    <div className="wsm-pack-form-section__heading">
                      <div className="wsm-pack-form-section__heading-title">
                        <span className="wsm-pack-form-section__heading-icon"><Tags aria-hidden="true" /></span>
                        <div>
                        <h3>Keywords</h3>
                        <p>Add terms that will help users find this pack in the register.</p>
                        </div>
                      </div>
                    </div>
                    <Keywords
                    values={values}
                    handleInputChange={handleInputChange}
                  />
                  </section>
                  {saveErrorAlert}
                  </div>
                </div>}

                {values?.current_section === 'users' && <>
                  <Users
                    allUsers={values?.users || []}
                    handleInputChange={handleInputChange}
                    userTemplate={createPackDefaults.users.esusers[0]}
                    plannedStartDate={values?.location?.planned_start_date}
                    plannedEndDate={values?.location?.planned_end_date}
                    handleAddFormRow={handleAddFormRow}
                    handleRemoveFormRow={handleRemoveFormRow}
                    errors={errors}
                    values={values}
                  />
                </>}

                {values?.current_section === 'forms' && <>
                  <Forms
                    values={values}
                    allForms={values?.forms || []}
                    allUserData={values?.users || {}}
                    handleInputChange={handleInputChange}
                    handleAddFormRow={handleAddFormRow}
                    handleRemoveFormRow={handleRemoveFormRow}
                    conversionUnit={values?.conversion_unit}
                    errors={errors}
                  />
                </>}

                {values?.current_section === 'uploads' && <>
                  <Uploads
                    error={uploadSaveError}
                    media={values?.media || []}
                    onFilesChange={saveAttachmentSelection}
                    onOpenFile={openExistingFile}
                    saving={draftSaveState === "saving"}
                    toBeUploaded={toBeUploaded}
                  />
                </>}
              </div>
            </div>
          </div>

          {values?.current_section !== 'location' && saveErrorAlert}

          <div className="button-group pack-controls">
            <div className="pack-controls--left">
              {editing && values?.current_section !== 'location' && (
                <Button
                  name="reset"
                  action="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEditPack('back');
                  }}
                >
                  Back
                  <FontAwesomeIcon
                    icon={faStepBackward}
                  />
                </Button>
              )}
            </div>
            <div className="pack-controls--right">
              {!editing && values?.current_section === 'location' && (
                <Button
                  name="submit"
                  action="primary"
                  onClick={handleCreatePack}
                >
                  Next
                  <FontAwesomeIcon
                    icon={faStepForward}
                  />
                </Button>
              )}

              {editing && values?.current_section !== 'uploads' && (
                <Button
                  name="submit"
                  action="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleEditPack('forward');
                  }}
                >
                  Next
                  <FontAwesomeIcon
                    icon={faStepForward}
                  />
                </Button>
              )}

              {values?.current_section === 'uploads' && (
                <>
                  <Button
                    name="submit"
                    action="primary"
                    onClick={handleCompletePack}
                  >
                    Create Pack
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>}

        <ConfirmationDialog
          cancelLabel="Cancel"
          confirmLabel="Remove"
          description="This action cannot be undone."
          onCancel={() => {
            setShowConfirmModal(false);
            setRemoveRowParams(null);
          }}
          onConfirm={confirmRemoveRow}
          open={showConfirmModal}
          title="Remove item"
          variant="destructive"
        >
          <p>{confirmMessage}</p>
        </ConfirmationDialog>

        
        {hasRole(authUser, ROLE_NAMES.SUPER_ADMIN) && <textarea
          rows='20'
          readOnly
          style={{ 'width': '100%' }}
          label="Data"
          value={JSON.stringify(values, null, 4)}
        >
        </textarea>}
      </div>
        </div>
      </CreatePackWorkflowFrame>
    </>
  );
};

export default CreatePack;
